import { Link } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import type { RefObject } from 'react';
import { type GestureResponderEvent, Platform } from 'react-native';
import type { EnrichedTextInputInstance } from 'react-native-enriched-html';
import { StyleSheet } from 'react-native-unistyles';
import {
  type ChatMessageActionSheetData,
  type ChatMessageActionSheetModal,
  useChatDetailContext,
} from '@/components/Chat/ChatDetailContext';
import RichLink from '@/components/Custom/RichLink';
import Text from '@/components/UI/Text';
import useChatActionItems from '@/hooks/useChatActionItems';
import getLinkSegments from '@/utils/getLinkSegments';
import getPublicUrl from '@/utils/getPublicUrl';
import handleLongPress from '@/utils/handleLongPress';
import type { ResolvedMessageSegment } from '@/utils/messageMentions';

type InlineMessageToken =
  | { type: 'text'; key: string; content: string }
  | { type: 'link'; key: string; content: string }
  | { type: 'mention'; key: string; label: string };

type MessageContentGroup =
  | { type: 'inline'; key: string; tokens: InlineMessageToken[] }
  | { type: 'richLink'; key: string; content: string };

function getMessageContentGroups(messageSegments: ResolvedMessageSegment[]) {
  const groups: MessageContentGroup[] = [];
  let inlineTokens: InlineMessageToken[] = [];

  const flushInlineTokens = () => {
    const firstToken = inlineTokens[0];
    if (!firstToken) return;

    groups.push({
      type: 'inline',
      key: `inline:${firstToken.key}`,
      tokens: inlineTokens,
    });
    inlineTokens = [];
  };

  for (const messageSegment of messageSegments) {
    if (messageSegment.type === 'mention') {
      inlineTokens.push({
        type: 'mention',
        key: messageSegment.key,
        label: messageSegment.label,
      });
      continue;
    }

    const linkSegments = getLinkSegments(messageSegment.text, getPublicUrl());
    for (const [linkSegmentIndex, linkSegment] of linkSegments.entries()) {
      const key = `${messageSegment.key}:${linkSegmentIndex}`;
      if (linkSegment.isMatch) {
        flushInlineTokens();
        groups.push({ type: 'richLink', key, content: linkSegment.content });
        continue;
      }

      inlineTokens.push({
        type: linkSegment.isLink ? 'link' : 'text',
        key,
        content: linkSegment.content,
      });
    }
  }

  flushInlineTokens();
  return groups;
}

function presentLinkActionSheet(
  ref: RefObject<ChatMessageActionSheetModal | null>,
  inputRef: RefObject<EnrichedTextInputInstance | null>,
  items: ChatMessageActionSheetData,
) {
  inputRef.current?.blur();
  ref.current?.present(items);
}

function ChatMessageContent({
  messageSegments,
  editedAt,
}: {
  messageSegments: ResolvedMessageSegment[];
  editedAt: string | null | undefined;
}) {
  const bottomSheetRef = useChatDetailContext((state) => state.bottomSheetRef);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const getActionItems = useChatActionItems();
  const onLinkPress = (e: GestureResponderEvent, linkContent: string) => {
    if (Platform.OS !== 'web' && e) {
      e.preventDefault();
      WebBrowser.openBrowserAsync(linkContent);
    }
  };
  const onLinkLongPress = (linkContent: string) => {
    presentLinkActionSheet(
      bottomSheetRef,
      inputRef,
      getActionItems('', linkContent, '', false),
    );
  };
  const contentGroups = getMessageContentGroups(messageSegments);
  const lastGroup = contentGroups.at(-1);

  return (
    <>
      {contentGroups.map((contentGroup) =>
        contentGroup.type === 'richLink' ? (
          <RichLink
            key={contentGroup.key}
            content={contentGroup.content}
            onLongPress={onLinkLongPress}
          />
        ) : (
          <Text key={contentGroup.key} size="md">
            {contentGroup.tokens.map((token) => {
              if (token.type === 'mention') {
                return (
                  <Text
                    key={token.key}
                    style={style.mention}
                    testID="ChatMessageMention"
                  >
                    {token.label}
                  </Text>
                );
              }

              if (token.type === 'link') {
                return (
                  <Link
                    key={token.key}
                    href={{ pathname: token.content }}
                    target="_blank"
                  >
                    <Text
                      onPress={(event) => onLinkPress(event, token.content)}
                      onLongPress={handleLongPress(() =>
                        onLinkLongPress(token.content),
                      )}
                      variant="primary"
                    >
                      {token.content}
                    </Text>
                  </Link>
                );
              }

              return <Text key={token.key}>{token.content}</Text>;
            })}
            {editedAt && contentGroup === lastGroup ? (
              <Text variant="typographySecondary" size="xs">
                {' '}
                (edited)
              </Text>
            ) : null}
          </Text>
        ),
      )}
      {editedAt && lastGroup?.type !== 'inline' ? (
        <Text variant="typographySecondary" size="xs">
          (edited)
        </Text>
      ) : null}
    </>
  );
}
export default ChatMessageContent;

const style = StyleSheet.create((theme) => ({
  mention: {
    color: theme.colors.info,
    backgroundColor: theme.colors.surfaceSecondary,
  },
}));
